import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowRight, FaPlay } from "react-icons/fa";
import OptimizedImage from "../common/OptimizedImage";
import hqWallpaper from "../../assets/hq-wallpaper.webp";

// Import hero slider images
import bibleAiChat from "../../assets/hero-slider/bible-ai-chat.webp";
import bibleStudyProgress from "../../assets/hero-slider/bible-study-progress.webp";
import bibleVersions from "../../assets/hero-slider/bible-versions.webp";
import bookLibrary from "../../assets/hero-slider/book-library.webp";
import favBibleVerses from "../../assets/hero-slider/fav-bible-verses.webp";
import growthProfile from "../../assets/hero-slider/growth-profile.webp";
import journalAndNotes from "../../assets/hero-slider/journal-and-notes.webp";

const HeroSection: React.FC = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    { image: bibleAiChat, label: "AI Chat Assistant" },
    { image: bibleStudyProgress, label: "Study Progress" },
    { image: bibleVersions, label: "Bible Versions" },
    { image: bookLibrary, label: "Book Library" },
    { image: favBibleVerses, label: "Favorite Verses" },
    { image: growthProfile, label: "Growth Profile" },
    { image: journalAndNotes, label: "Journal & Notes" },
  ];

  // Auto-advance slides every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const scrollToDashboard = () => {
    const element = document.getElementById("dashboard-preview");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleGetStarted = () => {
    // Redirect to the actual dashboard
    window.location.href = "https://christreignslab.com/dashboard";
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-16 lg:pt-0">
      {/* HQ Wallpaper Background */}
      <div
        className="absolute inset-0 z-0 hero-bg"
        style={{
          backgroundImage: `url(${hqWallpaper})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      ></div>

      {/* Gradient Overlay for Better Text Readability */}
      <div className="absolute inset-0 bg-sky-900 bg-opacity-60 z-10"></div>

      {/* Subtle Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-10 z-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      ></div>

      <div className="container mx-auto px-0 py-5 md:py-10 lg:py-40 xl:py-20 relative z-30">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-0 items-center">
          {/* Left Content */}
          <div className="px-5 lg:px-6 text-white space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-sm">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span>{t("hero.badge")}</span>
              </div>

              {/* Logo and Title Section */}
              {/* <div className="flex items-center space-x-2">
                <img
                  src={christReignsLogo}
                  alt="Christ Reigns Logo"
                  className="w-full h-full object-contain filter drop-shadow-2xl"
                />
              </div> */}

                <h1 className="text-white text-2xl md:text-5xl font-bold tracking-tight ">
                {t("hero.title")}
                </h1>

              <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed">
                {t("hero.description")}
              </p>
            </div>

            {/* Key Benefits */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                <span>{t("hero.keyBenefits.aiChat")}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                <span>{t("hero.keyBenefits.growth")}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                <span>{t("hero.keyBenefits.versions")}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-pink-400 rounded-full"></span>
                <span>{t("hero.keyBenefits.plans")}</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://christreignsmedia.io/register"
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center justify-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <span>{t("hero.startJourney")}</span>
                <FaArrowRight className="w-4 h-4" />
              </a>

              <button
                onClick={scrollToDashboard}
                className="bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center justify-center space-x-2 transition-all duration-300"
              >
                <FaPlay className="w-4 h-4" />
                <span>{t("hero.seeDashboard")}</span>
              </button>
            </div>
          </div>

          {/* Right Content - Dashboard Preview */}
          <div className="relative w-full px-0 lg:px-6">
            {/* Mobile: Full-width slider */}
            <div className="lg:hidden relative w-screen -mx-0 left-0">
              <div className="relative overflow-hidden bg-white/5">
                {/* Slides */}
                <div
                  className="flex transition-transform duration-700 ease-in-out h-[400px] sm:h-[450px]"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {slides.map((slide, index) => (
                    <div
                      key={index}
                      className="min-w-full h-full flex items-center justify-center"
                    >
                      <img
                        src={slide.image}
                        alt={slide.label}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ))}
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 z-10"
                  aria-label="Previous slide"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 z-10"
                  aria-label="Next slide"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>

                {/* Slide Indicators */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-1.5 z-10">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        currentSlide === index
                          ? "bg-white w-6"
                          : "bg-white/50 hover:bg-white/75 w-1.5"
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Slide Label */}
                <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg">
                  {slides[currentSlide].label}
                </div>

                {/* Slide Counter */}
                <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-semibold">
                  {currentSlide + 1} / {slides.length}
                </div>
              </div>
            </div>

            {/* Desktop: Boxed slider with border */}
            <div className="hidden lg:block relative bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20 shadow-2xl">
              <div className="relative overflow-hidden rounded-xl">
                <div className="relative h-[550px] lg:h-[500px] xl:h-[600px] overflow-hidden bg-white/5">
                  {/* Slides */}
                  <div
                    className="flex transition-transform duration-700 ease-in-out h-full"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  >
                    {slides.map((slide, index) => (
                      <div
                        key={index}
                        className="min-w-full h-full flex items-center justify-center"
                      >
                        <img
                          src={slide.image}
                          alt={slide.label}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Navigation Arrows */}
                  <button
                    onClick={prevSlide}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 z-10 group"
                    aria-label="Previous slide"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 z-10 group"
                    aria-label="Next slide"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>

                  {/* Slide Indicators */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                    {slides.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          currentSlide === index
                            ? "bg-white w-8"
                            : "bg-white/50 hover:bg-white/75 w-2"
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>

                  {/* Slide Label */}
                  <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                    {slides[currentSlide].label}
                  </div>

                  {/* Slide Counter */}
                  <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {currentSlide + 1} / {slides.length}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Cards - Hidden on mobile for cleaner look */}
            <div className="hidden lg:block absolute -top-4 -right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg animate-bounce">
              100% {t("hero.complete")}
            </div>
            <div className="hidden lg:block absolute -bottom-4 -left-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
              {t("hero.aiReady")}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
